import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError } from '../api';
import { money, qty, date, unitPrice, useSubscriptions, useTenantCatalog } from '../hooks';
import { Modal } from '../components/Modal';
import { Icon } from '../shared/Icon';
import type { Draft, Subscription } from '../types';

/** Договоры: кто на каком тарифе и в каком масштабе. Здесь же — предпросмотр расчёта. */
export function ContractsPage() {
  const { tenantId = '' } = useParams();
  const queryClient = useQueryClient();
  const catalog = useTenantCatalog(tenantId);
  const subscriptions = useSubscriptions(tenantId);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewFor, setPreviewFor] = useState<Subscription | null>(null);
  const [preview, setPreview] = useState<Draft | null>(null);
  const [period, setPeriod] = useState(defaultPeriod());
  const [form, setForm] = useState({
    customerId: '',
    planId: '',
    code: '',
    quantity: 1,
    startsOn: new Date().toISOString().slice(0, 10),
  });

  const create = useMutation({
    mutationFn: () => api.post(`/tenants/${tenantId}/subscriptions`, form),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions', tenantId] });
      setCreating(false);
    },
    onError: (err) => setError((err as ApiError).message),
  });

  const runPreview = useMutation({
    mutationFn: (subscription: Subscription) =>
      api.post<Draft>(`/tenants/${tenantId}/invoice-runs/preview`, {
        subscriptionId: subscription.id,
        periodStart: period.start,
        periodEnd: period.end,
      }),
    onSuccess: (draft) => setPreview(draft),
    onError: (err) => setError((err as ApiError).message),
  });

  const rows = subscriptions.data?.content ?? [];

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Договоры</h1>
          <div className="muted small">кто на каком тарифе</div>
        </div>
        <div className="spacer" />
        <button className="btn btn-primary" onClick={() => { setError(null); setCreating(true); }}>
          <Icon name="plus" />
          Новый договор
        </button>
      </div>

      {subscriptions.isLoading ? (
        <div className="state">Загрузка…</div>
      ) : rows.length === 0 ? (
        <div className="card">
          <div className="state-title">Договоров пока нет</div>
          <p className="muted">Договор связывает абонента с тарифом — с него начинается выставление счетов.</p>
          <div className="state-actions">
            <button className="btn btn-primary" onClick={() => setCreating(true)}>
              <Icon name="plus" />
              Заключить договор
            </button>
          </div>
        </div>
      ) : (
        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th>Код</th>
                <th>Абонент</th>
                <th>Тариф</th>
                <th className="num">Количество</th>
                <th>Период</th>
                <th>Статус</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.id}>
                  <td>
                    <code>{s.code}</code>
                  </td>
                  <td>{catalog.customerById.get(s.customerId)?.name ?? '—'}</td>
                  <td>{catalog.planById.get(s.planId)?.name ?? '—'}</td>
                  <td className="num">{s.quantity}</td>
                  <td className="muted small">
                    с {date(s.startsOn)}
                    {s.endsOn ? ` по ${date(s.endsOn)}` : ''}
                  </td>
                  <td>
                    {s.status === 'ACTIVE' ? (
                      <span className="badge badge-green">действует</span>
                    ) : s.status === 'PAUSED' ? (
                      <span className="badge badge-amber">приостановлено</span>
                    ) : (
                      <span className="badge badge-gray">завершено</span>
                    )}
                  </td>
                  <td className="num">
                    <button
                      className="btn btn-secondary btn-xs"
                      onClick={() => {
                        setError(null);
                        setPreview(null);
                        setPreviewFor(s);
                        runPreview.mutate(s);
                      }}
                    >
                      Расчёт
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <Modal title="Новый договор" onClose={() => setCreating(false)}>
          <div className="row">
            <div className="form-group">
              <label className="form-label" htmlFor="sub-customer">
                Абонент
              </label>
              <select
                id="sub-customer"
                className="input"
                value={form.customerId}
                onChange={(e) => setForm({ ...form, customerId: e.target.value })}
              >
                <option value="">— выберите —</option>
                {catalog.customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="sub-plan">
                Тариф
              </label>
              <select
                id="sub-plan"
                className="input"
                value={form.planId}
                onChange={(e) => setForm({ ...form, planId: e.target.value })}
              >
                <option value="">— выберите —</option>
                {catalog.plans.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="row">
            <div className="form-group">
              <label className="form-label" htmlFor="sub-code">
                Номер договора
              </label>
              <input
                id="sub-code"
                className="input"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="sub-qty">
                Количество
              </label>
              <input
                id="sub-qty"
                className="input"
                type="number"
                min={1}
                value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: Number(e.target.value) })}
              />
              <div className="form-hint">Терминалы, места, устройства — на это множатся компоненты.</div>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="sub-start">
              Начало
            </label>
            <input
              id="sub-start"
              className="input"
              type="date"
              value={form.startsOn}
              onChange={(e) => setForm({ ...form, startsOn: e.target.value })}
            />
          </div>

          {error && <div className="form-error">{error}</div>}

          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={() => setCreating(false)}>
              Отмена
            </button>
            <button
              className="btn btn-primary"
              disabled={!form.customerId || !form.planId || !form.code || create.isPending}
              onClick={() => { setError(null); create.mutate(); }}
            >
              Заключить
            </button>
          </div>
        </Modal>
      )}

      {previewFor && (
        <Modal title={`Расчёт по договору «${previewFor.code}»`} onClose={() => setPreviewFor(null)}>
          <div className="row">
            <div className="form-group">
              <label className="form-label" htmlFor="preview-from">
                Период с
              </label>
              <input
                id="preview-from"
                className="input"
                type="date"
                value={period.start}
                onChange={(e) => setPeriod({ ...period, start: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="preview-to">
                по
              </label>
              <input
                id="preview-to"
                className="input"
                type="date"
                value={period.end}
                onChange={(e) => setPeriod({ ...period, end: e.target.value })}
              />
              <div className="form-hint">Конец периода не включается.</div>
            </div>
          </div>

          <button
            className="btn btn-secondary btn-sm"
            onClick={() => runPreview.mutate(previewFor)}
            disabled={runPreview.isPending}
          >
            <Icon name="refresh" />
            Пересчитать
          </button>

          {error && <div className="form-error">{error}</div>}
          {runPreview.isPending && <div className="state">Считаем…</div>}

          {preview && (
            <>
              <table className="table" style={{ marginTop: 12 }}>
                <thead>
                  <tr>
                    <th>Позиция</th>
                    <th className="num">Количество</th>
                    <th className="num">Цена</th>
                    <th className="num">Сумма</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.lines.map((line, i) => (
                    <tr key={i}>
                      <td>{line.description}</td>
                      <td className="num">{qty(line.quantity)}</td>
                      <td className="num">{unitPrice(line)}</td>
                      <td className="num">{money(line.amountKopecks)}</td>
                    </tr>
                  ))}
                  {preview.lines.length === 0 && (
                    <tr>
                      <td colSpan={4} className="state">
                        За этот период начислять нечего.
                      </td>
                    </tr>
                  )}
                </tbody>
                <tfoot>
                  <tr>
                    <th colSpan={3}>Итого</th>
                    <th className="num">{money(preview.totalKopecks)} ₽</th>
                  </tr>
                </tfoot>
              </table>
              <div className="form-hint">
                Это только расчёт: счёт не выставлен и потребление не помечено оплаченным.
              </div>
            </>
          )}
        </Modal>
      )}
    </>
  );
}

/** По умолчанию считаем прошлый календарный месяц — самый частый вопрос к системе. */
function defaultPeriod() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const end = new Date(now.getFullYear(), now.getMonth(), 1);
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return { start: iso(start), end: iso(end) };
}
